import { Close, Computer, Smartphone } from "@mui/icons-material";
import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";

import { DateTimeLabel } from "../../components/DateTimeLabel";
import { getApiErrorMessage } from "../../helpers/api/getApiErrorMessage";
import { EmailLog } from "../../interfaces/emailLog";
import { ApiResponse } from "../../interfaces/paginated";
import { api } from "../../services/api";
import { EmailLogStatusChip, emailLogSource, formatAddresses } from "./helpers";

interface Props {
    emailLogId: number | null;
    onClose: () => void;
}

type PreviewWidth = "desktop" | "mobile";

const withBaseTarget = (html: string) =>
    html.includes("<head")
        ? html.replace(/<head([^>]*)>/i, '<head$1><base target="_blank">')
        : `<base target="_blank">${html}`;

function DetailRow({
    label,
    children,
}: {
    label: string;
    children: ReactNode;
}) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "160px 1fr" },
                gap: { xs: 0.25, sm: 2 },
                py: 1,
                borderBottom: "1px solid #eee",
            }}
        >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {label}
            </Typography>
            <Typography
                variant="body2"
                component="div"
                sx={{ overflowWrap: "anywhere" }}
            >
                {children}
            </Typography>
        </Box>
    );
}

export function EmailLogDetailsModal({ emailLogId, onClose }: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [emailLog, setEmailLog] = useState<EmailLog | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [tab, setTab] = useState(0);
    const [previewWidth, setPreviewWidth] = useState<PreviewWidth>("desktop");

    useEffect(() => {
        if (!emailLogId) {
            return;
        }

        setTab(0);
        setEmailLog(null);
        setError(null);
        setLoading(true);

        api.get<ApiResponse<EmailLog>>(`/email-logs/${emailLogId}`)
            .then((response) => setEmailLog(response.data.response))
            .catch((requestError) =>
                setError(
                    getApiErrorMessage(
                        requestError,
                        "Não foi possível carregar o e-mail."
                    )
                )
            )
            .finally(() => setLoading(false));
    }, [emailLogId]);

    return (
        <Dialog
            open={Boolean(emailLogId)}
            onClose={onClose}
            fullScreen={isMobile}
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle sx={{ pr: 6 }}>
                <Typography
                    variant="h6"
                    component="span"
                    sx={{ display: "block", overflowWrap: "anywhere" }}
                >
                    {emailLog?.subject || "Detalhes do e-mail"}
                </Typography>
                <IconButton
                    onClick={onClose}
                    title="Fechar"
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: { xs: 1.5, sm: 3 } }}>
                {loading && (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress />
                    </Box>
                )}

                {error && <Alert severity="error">{error}</Alert>}

                {emailLog && (
                    <>
                        <Tabs
                            value={tab}
                            onChange={(_, value) => setTab(value)}
                            variant="scrollable"
                            allowScrollButtonsMobile
                            sx={{ mb: 2 }}
                        >
                            <Tab label="Conteúdo" />
                            <Tab label="Texto" />
                            <Tab label="Detalhes" />
                        </Tabs>

                        {tab === 0 &&
                            (emailLog.html_body ? (
                                <Stack spacing={1.5} alignItems="center">
                                    <ToggleButtonGroup
                                        size="small"
                                        exclusive
                                        value={previewWidth}
                                        onChange={(_, value) =>
                                            value && setPreviewWidth(value)
                                        }
                                    >
                                        <ToggleButton value="desktop">
                                            <Computer fontSize="small" />
                                            &nbsp;Desktop
                                        </ToggleButton>
                                        <ToggleButton value="mobile">
                                            <Smartphone fontSize="small" />
                                            &nbsp;Celular
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                    <Box
                                        component="iframe"
                                        title="Conteúdo do e-mail"
                                        sandbox="allow-popups allow-popups-to-escape-sandbox"
                                        srcDoc={withBaseTarget(
                                            emailLog.html_body
                                        )}
                                        sx={{
                                            width: "100%",
                                            maxWidth:
                                                previewWidth === "mobile"
                                                    ? 375
                                                    : "100%",
                                            height: { xs: "70vh", sm: "65vh" },
                                            border: "1px solid #ddd",
                                            borderRadius: 1,
                                            background: "#fff",
                                        }}
                                    />
                                </Stack>
                            ) : (
                                <Alert severity="info">
                                    Este e-mail não possui conteúdo HTML.
                                </Alert>
                            ))}

                        {tab === 1 &&
                            (emailLog.text_body ? (
                                <Box
                                    component="pre"
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        overflowWrap: "anywhere",
                                        fontFamily: "inherit",
                                        fontSize: 14,
                                        m: 0,
                                    }}
                                >
                                    {emailLog.text_body}
                                </Box>
                            ) : (
                                <Alert severity="info">
                                    Este e-mail não possui versão em texto.
                                </Alert>
                            ))}

                        {tab === 2 && (
                            <Box>
                                <DetailRow label="Status">
                                    <EmailLogStatusChip
                                        status={emailLog.status}
                                    />
                                </DetailRow>
                                {emailLog.error && (
                                    <DetailRow label="Erro">
                                        <Typography
                                            variant="body2"
                                            color="error"
                                            component="span"
                                        >
                                            {emailLog.error}
                                        </Typography>
                                    </DetailRow>
                                )}
                                <DetailRow label="Criado em">
                                    <DateTimeLabel
                                        value={emailLog.created_at}
                                    />
                                </DetailRow>
                                <DetailRow label="Enviado em">
                                    <DateTimeLabel
                                        value={emailLog.sent_at ?? undefined}
                                    />
                                </DetailRow>
                                {emailLog.failed_at && (
                                    <DetailRow label="Falhou em">
                                        <DateTimeLabel
                                            value={emailLog.failed_at}
                                        />
                                    </DetailRow>
                                )}
                                <DetailRow label="Remetente">
                                    {formatAddresses(
                                        emailLog.from_address
                                            ? [
                                                  {
                                                      address:
                                                          emailLog.from_address,
                                                      name:
                                                          emailLog.from_name ??
                                                          undefined,
                                                  },
                                              ]
                                            : null
                                    )}
                                </DetailRow>
                                <DetailRow label="Para">
                                    {formatAddresses(emailLog.to)}
                                </DetailRow>
                                <DetailRow label="Cópia (CC)">
                                    {formatAddresses(emailLog.cc)}
                                </DetailRow>
                                <DetailRow label="Cópia oculta (CCO)">
                                    {formatAddresses(emailLog.bcc)}
                                </DetailRow>
                                <DetailRow label="Responder para">
                                    {formatAddresses(emailLog.reply_to)}
                                </DetailRow>
                                <DetailRow label="Anexos">
                                    {emailLog.attachments?.length
                                        ? emailLog.attachments.join(", ")
                                        : "-"}
                                </DetailRow>
                                <DetailRow label="Origem">
                                    {emailLogSource(emailLog)}
                                </DetailRow>
                                <DetailRow label="Disparado por">
                                    {emailLog.user?.name ??
                                        "Sistema (automático)"}
                                </DetailRow>
                                <DetailRow label="ID da mensagem">
                                    {emailLog.message_id ?? "-"}
                                </DetailRow>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
